// =============================================================================
// selectors/goalForm.ts — the goal editor's seam.
// -----------------------------------------------------------------------------
// ⚠ `ui/` may not import `domain/` at all, so the rules travel through here.
//
// ── 🚨 THE BOOLEAN LISTS ARE COMPOSED HERE, NOT IN `domain/` ──
//
// Two of the four templates point at a discipline flag, and the old client
// builds their option lists from two registries:
//
//   boolean-adherence → TRADE_BOOLEANS ++ JOURNAL_BOOLEANS
//   streak            → JOURNAL_BOOLEANS only
//
// `TRADE_BOOLEANS` already exists one file over, and re-typing those eight rows
// inside `domain/` would create a second registry that can silently disagree
// with the trade form's — the exact thing the bulk editor's own comment warns
// about. So the composition lives at this layer, where the existing list is
// reachable, and `domain/goalForm.ts` stays free of registries.
// =============================================================================

import { TRADE_BOOLEANS } from './tradeForm';

export {
  GOAL_PERIOD_OPTIONS, GOAL_TEMPLATES, TEMPLATE_PARAM_KEYS,
  draftFromGoal, goalStorageName, goalType, mergeGoalParams, newGoalDraft,
  orderAccountsForPicker, ownedParams, paramChoice, paramNumber,
  templateIsMeasured, toggleAccountId,
} from '@domain/goalForm';

export type { GoalDraft, PickerAccount } from '@domain/goalForm';

/**
 * The day-level flags, transcribed from the old client's `JOURNAL_BOOLEANS`.
 *
 * ⚠ ONE ENTRY, and that is current rather than a stub. Beta's own comment says
 * three others (`stoppedWhenStoppedOut`, `didNotOvertrade`, `stuckToAPlus`) were
 * removed because they had no input anywhere and duplicated per-trade flags that
 * already feed the discipline score. Its migration nulls any goal still pointing
 * at one — so a goal referencing a removed key is a shape that exists in old
 * exports and never in the live book.
 */
export const JOURNAL_BOOLEANS: readonly { key: string; label: string }[] = [
  { key: 'preMarketPlanFollowed', label: 'Followed pre-market plan' },
];

/** `boolean-adherence` — every flag, trade-level first. Beta's order. */
export const ADHERENCE_BOOLEANS: readonly { key: string; label: string }[] = [
  ...TRADE_BOOLEANS.map((b) => ({ key: b.key, label: b.label })),
  ...JOURNAL_BOOLEANS,
];

/** `streak` — day-level flags only. A streak counts DAYS, not trades. */
export const STREAK_BOOLEANS = JOURNAL_BOOLEANS;

/** `boolean-adherence`'s scope picker, in beta's order — `trades` renders first. */
export const ADHERENCE_SCOPES: readonly { value: string; label: string }[] = [
  { value: 'trades', label: 'Trades' },
  { value: 'days', label: 'Days' },
];
